/**
 * @name AuthIntegrationMiddleware
 * @description Middleware para detectar e processar autenticação transparente
 * @author Gabriel M. Guimarães | gabrielmg7
 * @package src/components/middleware/AuthIntegrationMiddleware.tsx
 */

import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useUserContext from '../../contexts/UserContext/useUserContext';
import IntegrationLoginPage from '../pages/Auth/SsoIntegration/IntegrationLoginPage';
import { useAuthIntegration } from '../pages/Auth/SsoIntegration/useAuthIntegration';

interface AuthIntegrationMiddlewareProps {
     children: React.ReactNode;
}

/**
 * Middleware que intercepta URLs com parâmetros de autenticação
 * e processa automaticamente a autenticação transparente
 *
 * Funcionalidades:
 * - Detecta parâmetros ?ano_letivo=2025&matricula=7227 na URL
 * - Processa autenticação automaticamente
 * - Exibe tela de loading durante o processo
 * - Redireciona após sucesso ou mostra erro
 */
const AuthIntegrationMiddleware: React.FC<AuthIntegrationMiddlewareProps> = ({ children }) => {
     const location = useLocation();
     const navigate = useNavigate();
     const { userSession } = useUserContext();
     const { authenticateFromExternalSystem, isProcessingIntegration, integrationError, errorType, responsavelData, resetProcessingState } = useAuthIntegration();

     const [hasIntegrationParams, setHasIntegrationParams] = useState(false);
     const [hasProcessedAuth, setHasProcessedAuth] = useState(false);

     // Refs para evitar processamento duplo
     const processedParamsRef = useRef<string>('');
     const isProcessingRef = useRef(false);

     // Detecta parâmetros de integração na URL
     useEffect(() => {
          const searchParams = new URLSearchParams(location.search);
          const anoLetivo = searchParams.get('ano_letivo');
          const matricula = searchParams.get('matricula');
          const organizacao = searchParams.get('organizacao');

          const hasParams = Boolean(anoLetivo && matricula);
          const currentParamsKey = `${anoLetivo}-${matricula}`;

          // OTIMIZAÇÃO: Evita reprocessamento múltiplo dos mesmos parâmetros
          if (processedParamsRef.current === currentParamsKey) {
               // console.log('⏸️ [Middleware] Parâmetros já processados, ignorando:', currentParamsKey);
               return;
          }

          setHasIntegrationParams(hasParams);

          // PROTEÇÃO: Só processa se há parâmetros, não está logado e não está processando
          if (hasParams && !userSession && !isProcessingRef.current && !isProcessingIntegration) {
               console.log('🎯 [Middleware] Processando parâmetros de integração:', {
                    anoLetivo,
                    matricula,
                    organizacao,
                    currentPath: location.pathname,
               });

               // Marca parâmetros como processados IMEDIATAMENTE
               processedParamsRef.current = currentParamsKey;
               isProcessingRef.current = true;
               setHasProcessedAuth(true); // Remove parâmetros da URL imediatamente
               const removeUrlParams = () => {
                    const newUrl = window.location.pathname;
                    window.history.replaceState({}, '', newUrl);
               };

               // CORREÇÃO: Remove delay desnecessário - executa imediatamente
               console.log('🌐 [Middleware] Removendo parâmetros da URL');
               removeUrlParams();

               // Processa autenticação
               authenticateFromExternalSystem(anoLetivo!, matricula!).finally(() => {
                    isProcessingRef.current = false;
               });
          }
     }, [location.search, userSession, hasProcessedAuth, isProcessingIntegration, authenticateFromExternalSystem]);

     // Limpa estados de integração quando usuário se autentica com sucesso
     useEffect(() => {
          if (userSession && hasProcessedAuth) {
               console.log('✅ [Middleware] Usuário autenticado com sucesso, limpando estados de integração');
               setHasIntegrationParams(false);
               setHasProcessedAuth(false);

               // Força redirecionamento se ainda estiver na página de login
               if (location.pathname === '/login') {
                    console.log('🏠 [Middleware] Redirecionando usuário autenticado para dashboard');
                    navigate('/', { replace: true });
               }
          }
     }, [userSession, hasProcessedAuth, location.pathname]);

     // IMPORTANTE: Se tem parâmetros de integração OU está processando OU tem erro, mostra a página de integração
     // Mas NÃO mostra se o usuário já está autenticado (para permitir navegação normal)
     // Também não mostra se não está mais processando E não há erro (sucesso)
     const shouldShowIntegrationPage = hasIntegrationParams && !userSession && (isProcessingIntegration || integrationError || errorType || (!hasProcessedAuth && !isProcessingIntegration));

     if (shouldShowIntegrationPage) {
          console.log('🔍 [Middleware] Exibindo IntegrationLoginPage:', {
               hasIntegrationParams,
               userSession: !!userSession,
               isProcessingIntegration,
               integrationError,
               errorType,
               hasProcessedAuth,
               shouldShow: shouldShowIntegrationPage,
          });

          // 🔧 Passa dados do hook como props para evitar múltiplas instâncias
          return (
               <IntegrationLoginPage
                    isProcessingIntegration={isProcessingIntegration}
                    integrationError={integrationError}
                    errorType={errorType}
                    responsavelData={responsavelData}
                    resetProcessingState={resetProcessingState}
               />
          );
     }

     // Caso contrário, renderiza normalmente os children
     return <>{children}</>;
};

export default AuthIntegrationMiddleware;
